"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader, ShieldCheck } from "lucide-react";
function QuoteForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4 animate-in fade-in zoom-in duration-500">
        <div className="size-16 bg-primary/10 rounded-lg flex items-center justify-center mb-4">
          <ShieldCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">
          Application Received!
        </h3>
        <p className="text-muted-foreground max-w-md font-sans">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 rounded-lg border-primary text-primary hover:bg-primary/5">
          Submit Another Application
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="fullName" rules={{
        required: "Full name is required",
        minLength: {
          value: 2,
          message: "Minimum 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold">Full Name</FormLabel>
              <FormControl>
                <Input className="rounded-md border-border focus:ring-primary" placeholder="e.g. John Smith" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email address is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold">Business Email</FormLabel>
              <FormControl>
                <Input className="rounded-md border-border focus:ring-primary" type="email" placeholder="john@contracting.com" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="phone" rules={{
        required: "Phone number is required",
        minLength: {
          value: 10,
          message: "Minimum 10 digits"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold">Phone Number</FormLabel>
              <FormControl>
                <Input className="rounded-md border-border focus:ring-primary" type="tel" placeholder="(555) 000-0000" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="zipCode" rules={{
        required: "Zip code is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-sans font-semibold">Service Area Zip Code</FormLabel>
              <FormControl>
                <Input className="rounded-md border-border focus:ring-primary" placeholder="e.g. 32801" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="insuranceType" rules={{
        required: "Please select your primary trade"
      }} render={({
        field
      }) => <FormItem className="md:col-span-2">
              <FormLabel className="font-sans font-semibold">Primary Business Trade</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="rounded-md border-border focus:ring-primary">
                    <SelectValue placeholder="Select your trade" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  <SelectItem value="landscaper">Landscaping / Hardscaping</SelectItem>
                  <SelectItem value="pool">Pool Builder</SelectItem>
                  <SelectItem value="concrete">Concrete Contractor</SelectItem>
                  <SelectItem value="outdoor">Outdoor Living Specialist</SelectItem>
                  <SelectItem value="other">Other Construction Trade</SelectItem>
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-lg bg-primary hover:bg-primary/90 text-white font-default font-bold tracking-wide" size="lg">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Processing Application...
          </> : "Become a ClifRock Installer"}
      </Button>
      <p className="text-xs text-center text-muted-foreground mt-4 font-sans">
        By submitting, you agree to receive information about the ClifRock Authorized Installer Program.
      </p>
    </Form>;
}
export default function JoinTheNetwork() {
  return <section data-section-id="256210" id="join-the-network" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/5 blur-3xl" />
        <div className="absolute -bottom-[20%] -left-[10%] w-[50%] h-[50%] rounded-lg bg-secondary/5 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-10">
          <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            Join the ClifRock Network
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Scale your business with faster installations and less labor. Provide your details below to start your application for the Authorized Installer Program.
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          <Card className="shadow-xl border-border/50 rounded-xl overflow-hidden">
            <CardHeader className="bg-muted/50 border-b border-border/50 pb-6">
              <CardTitle className="text-2xl font-default font-bold text-foreground">
                Installer Application
              </CardTitle>
              <CardDescription className="font-sans text-muted-foreground">
                Your professional information is secure. Complete the fields below to request contractor information.
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <FormProvider formKey="join_network_form_apply_now" formId="join_network_form_apply_now" sectionName="join-the-network" defaultValues={{
              fullName: "",
              email: "",
              phone: "",
              zipCode: "",
              insuranceType: ""
            }} submitText="Become a ClifRock Installer" successMessage="Thank you for your interest! Your application has been received. A ClifRock representative will contact you shortly to discuss training and support opportunities.">
                <QuoteForm />
              </FormProvider>
            </CardContent>
          </Card>
        </div>
      </div>
    </section>;
}